import { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Notification } from '../types';
import * as api from '../utils/supabase/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface NotificationBellProps {
  userId: string;
}

export function NotificationBell({ userId }: NotificationBellProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Add a small delay to ensure authentication is ready
    const timeout = setTimeout(() => {
      loadNotifications();
    }, 500);

    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [userId]);

  const loadNotifications = async () => {
    try {
      // Check if we have an access token before making the request
      const token = api.getAccessToken();
      if (!token) {
        console.log('No access token available yet for notifications');
        return;
      }
      
      const { notifications: data } = await api.getNotifications();
      setNotifications(data);
    } catch (error: any) {
      // Only log error if it's not a 401 (which means we're not authenticated yet)
      if (error.message && !error.message.includes('401') && !error.message.includes('Unauthorized')) {
        console.error('Error loading notifications:', error);
      }
      // Silently fail for auth errors to avoid cluttering the console during login
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAsRead = (id: string) => {
    // Optimistic update — mark read instantly, then sync with server
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
    api.markNotificationRead(id).catch((error) => {
      console.error('Error marking notification as read:', error);
      // Rollback
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: false } : n))
      );
      toast.error('Failed to mark notification as read');
    });
  };

  const handleMarkAllAsRead = () => {
    // Optimistic update — mark all read instantly, then sync with server
    const previous = notifications;
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setIsLoading(true);
    api.markAllNotificationsRead()
      .then(() => toast.success('All notifications marked as read'))
      .catch((error) => {
        console.error('Error marking all as read:', error);
        setNotifications(previous); // Rollback
        toast.error('Failed to mark all as read');
      })
      .finally(() => setIsLoading(false));
  };

  const handleDeleteNotification = (id: string) => {
    // Optimistic update — remove instantly, then sync with server
    const previous = notifications;
    setNotifications(prev => prev.filter(n => n.id !== id));
    api.deleteNotification(id).catch((error) => {
      console.error('Error deleting notification:', error);
      setNotifications(previous); // Rollback
      toast.error('Failed to delete notification');
    });
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return 'ℹ️';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-white/10 ring-2 ring-cyan-400/20 ring-offset-0 transition-all duration-200"
        >
          <Bell className="h-5 w-5 text-white drop-shadow-sm" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={isLoading}
            >
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-accent/50 transition-colors cursor-pointer ${
                    !notification.read ? 'bg-accent/20' : ''
                  }`}
                  onClick={() => !notification.read && handleMarkAsRead(notification.id)}
                >
                  <div className="flex gap-3">
                    <div className="text-xl flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm">{notification.title}</p>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                          })}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotification(notification.id);
                          }}
                          className="h-auto p-1 text-xs"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}